// "This email already has a Stratia account" (#5154, founder pick A).
//
// Firebase keeps one account per email. When a student taps Apple (or
// Facebook) with an address whose account signs in another way, Firebase
// answers auth/account-exists-with-different-credential and hands back the
// credential it could not use. The sheet never looks up which way the
// account uses: that lookup would tell anyone whether an address has an
// account, which email enumeration protection exists to stop. The student
// signs in the way they did before, and only THEN is asked "Also sign in
// with Apple next time?"; Apple requires that explicit consent before an
// Apple account is linked to other data. Nothing is linked without the yes.
//
// The pending credential is kept in localStorage, not the tab: the email
// link opens in a new tab of the same browser. It lives 15 minutes (Apple's
// own token is good for about 10), is cleared on use, on "No thanks", on a
// different account signing in and on sign-out, and never rides an email
// link's carry or any analytics event.
import { OAuthCredential, linkWithCredential } from 'firebase/auth';
import { track, EVENTS } from './analytics';
import { PROVIDERS } from './signInProviders';

export const ACCOUNT_EXISTS_CODE = 'auth/account-exists-with-different-credential';
export const PENDING_LINK_KEY = 'stratia_signin_pending_link_v1';
export const PENDING_LINK_TTL_MS = 15 * 60 * 1000;

const norm = (v) => String(v || '').trim().toLowerCase();

/** Keep the credential an account-exists answer carried. Returns
 *  {provider, email} for the sheet (email may be '' when Firebase gave
 *  none; nothing is kept then), or null for an unknown provider. */
export function stashPendingLink(providerId, error, now = Date.now()) {
    const provider = PROVIDERS[providerId];
    let cred = null;
    try { cred = provider?.credentialFromError(error) || null; } catch { cred = null; }
    const email = norm(error?.customData?.email);
    if (!provider) return null;
    // Kept only with the address it belongs to: a credential with no address
    // could be offered to whichever account signs in next.
    if (cred && email && provider.linkable) {
        try {
            localStorage.setItem(PENDING_LINK_KEY, JSON.stringify({ provider: providerId, email, cred: cred.toJSON(), t: now }));
        } catch { /* storage off: the student still signs in, just no link offer */ }
    }
    return { provider: providerId, email };
}

/** The pending link, or null (expired and unreadable entries are cleared). */
export function pendingLink(now = Date.now()) {
    let raw = null;
    try { raw = localStorage.getItem(PENDING_LINK_KEY); } catch { return null; }
    if (!raw) return null;
    try {
        const p = JSON.parse(raw);
        if (p && PROVIDERS[p.provider]?.linkable && p.cred && p.email && typeof p.t === 'number' && now >= p.t && now - p.t < PENDING_LINK_TTL_MS) {
            return p;
        }
    } catch { /* unreadable */ }
    clearPendingLink();
    return null;
}

export function clearPendingLink() {
    try { localStorage.removeItem(PENDING_LINK_KEY); } catch { /* storage off */ }
}

/** The link to offer this signed-in user, or null. Offered only when the
 *  account's address is the one the provider gave (one account per email, so
 *  this is the account Firebase refused to make twice) and the provider is
 *  not already on it. A different account clears it. */
export function linkToOffer(user, now = Date.now()) {
    const p = pendingLink(now);
    if (!p || !user) return null;
    const linked = (user.providerData || []).some((d) => d?.providerId === PROVIDERS[p.provider].firebaseId);
    if (linked || norm(user.email) !== p.email) {
        clearPendingLink();
        return null;
    }
    return p;
}

/** How this account signs in now, for the consent's words. */
export function signedInWithName(user) {
    const ids = (user?.providerData || []).map((d) => d?.providerId);
    for (const p of Object.values(PROVIDERS)) if (ids.includes(p.firebaseId)) return p.name;
    return 'your email';
}

/** The student's answer. 'yes' links the pending credential; 'no' drops it.
 *  Resolves the outcome: 'linked' | 'declined' | 'failed'. Never throws. */
export async function answerLink(user, pending, answer, { link = linkWithCredential } = {}) {
    let outcome = 'declined';
    // Already on the account (a second tab's consent after the first said
    // yes, reviewer on #5154): nothing to add, and nothing failed.
    const already = (user?.providerData || []).some((d) => d?.providerId === PROVIDERS[pending.provider]?.firebaseId);
    if (answer === 'yes' && already) {
        outcome = 'linked';
    } else if (answer === 'yes') {
        try {
            await link(user, OAuthCredential.fromJSON(pending.cred));
            outcome = 'linked';
        } catch (e) {
            console.info('Link not added:', e?.code);
            outcome = 'failed';
        }
    }
    clearPendingLink();
    try { track(EVENTS.SIGNIN_LINKED, { added: pending.provider, outcome }); } catch { /* never blocks */ }
    return outcome;
}
